import { Component, DestroyRef, EventEmitter, OnDestroy, Output, inject } from "@angular/core";
import { CoreCommonModule } from "@core/common.module";
import { NgbModule } from "@ng-bootstrap/ng-bootstrap";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { UserService } from "@core/services/auth";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { CbcService } from "app/main/cbc/services/cbc.service";
import { CbcGrade } from "app/main/cbc/models/cbc";
import { ToastrService } from "ngx-toastr";
import { TermsAndConditionsComponent } from "app/main/pages/terms-and-conditions/terms-and-conditions.component";

@Component({
  standalone: true,
  imports: [CoreCommonModule, NgbModule, TermsAndConditionsComponent],
  selector: "app-register-form",
  templateUrl: "./register-form.html",
  styleUrls: ["./register-form.scss"],
})
export class RegisterFormComponent implements OnDestroy {
  @Output() registrationSuccess = new EventEmitter<{ username: string; password: string }>();

  registerForm: UntypedFormGroup;
  r_submitted = false;
  loading = false;
  passwordTextType = false;
  passwordTextType2 = false;
  cbcGrades: Array<CbcGrade> = [];
  agreedToTerms: boolean;

  signUpRoles = [
    { id: 30, role: "Student" },
    { id: 60, role: "Parent" },
  ];

  private unsubscribe$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private _formBuilder = inject(UntypedFormBuilder);
  private _userService = inject(UserService);
  private _helperFunctions = inject(HelperFunctionsService);
  private cbcService = inject(CbcService);
  private toastr = inject(ToastrService);

  constructor() {
    this.registerForm = this._formBuilder.group({
      phone: ["", Validators.required],
      name: ["", Validators.required],
      email: [""],
      role: [0, Validators.required],
      educationSystem: ["", Validators.required],
      grade: [""],
    });

    this.registerForm.get("educationSystem")?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const gradeControl = this.registerForm.get("grade");
        if (value === "CBC") {
          gradeControl?.setValidators(Validators.required);
        } else {
          gradeControl?.clearValidators();
          gradeControl?.setValue("");
        }
        gradeControl.updateValueAndValidity();
      });

    this.getCbcGrades();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  get rf() {
    return this.registerForm.controls;
  }

  togglePasswordTextType(): void {
    this.passwordTextType = !this.passwordTextType;
  }

  togglePasswordTextType2(): void {
    this.passwordTextType2 = !this.passwordTextType2;
  }

  roleChanged(): void {
    this.registerForm.addControl("password", new UntypedFormControl("", [Validators.required]));
    this.registerForm.addControl("confirmPassword", new UntypedFormControl("", [Validators.required]));
    this.registerForm.setValidators(this._helperFunctions.matchPassword("password", "confirmPassword"));

    if (this.rf.role.value == 30) {
      this.registerForm.controls["educationSystem"].setValidators(Validators.required);
    } else {
      this.registerForm.controls["educationSystem"].clearValidators();
    }
    this.registerForm.get("educationSystem")?.setValue("");
    this.registerForm.get("educationSystem")?.updateValueAndValidity();
    this.rf.password.updateValueAndValidity();
    this.rf.confirmPassword.updateValueAndValidity();
  }

  getCbcGrades(): void {
    this.cbcService
      .getDALSGrades()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => (this.cbcGrades = res),
      });
  }

  getAcceptedTermsStatus($event: boolean): void {
    this.agreedToTerms = $event;
  }

  onSubmitReg(): void {
    this.r_submitted = true;
    if (this.registerForm.invalid) return;

    this.loading = true;
    const userDetails = {
      firstName: this.registerForm.value.name,
      lastName: "",
      email: this.registerForm.value.email,
      phoneNumber: this.registerForm.value.phone,
      schoolRole: this.registerForm.value.role,
      password: this.registerForm.value.password,
      ...(this.registerForm.value.role === 30 && {
        educationSystem: this.registerForm.value.educationSystem,
      }),
      ...(this.registerForm.value.educationSystem === "CBC" && {
        grade: this.registerForm.value.grade,
      }),
    };
    this._userService.addUser(userDetails).subscribe({
      next: () => {
        this.loading = false;
        this.toastr.success("Registration successful", "Success!", {
          timeOut: 5000,
          toastClass: "toast ngx-toastr",
          closeButton: true,
        });
        this.registrationSuccess.emit({
          username: this.rf.phone.value,
          password: this.rf.password.value,
        });
      },
      error: (err) => {
        this.loading = false;
        let message = "Registration failed. Please try again.";
        try {
          let parsedError = typeof err?.error === "string" ? JSON.parse(err.error) : err?.error;
          if (typeof parsedError?.message === "string" && parsedError.message.trim().startsWith("{")) {
            parsedError = JSON.parse(parsedError.message);
          }
          message = parsedError?.message || message;
        } catch {
          // err.error wasn't valid JSON at either level — fall back to default message
        }
        this.toastr.warning(message, "Failed!", {
          timeOut: 5000,
          toastClass: "toast ngx-toastr",
          closeButton: true,
        });
      },
    });
  }

  /** Called by shell when switching back to register form */
  reset(): void {
    this.r_submitted = false;
    this.registerForm?.reset(this.registerForm.value);
  }
}